// ============================================
// Home Page - Main Landing Page
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper';
import { 
  FaHome, 
  FaChartBar, 
  FaEye, 
  FaFileAlt,
  FaWallet,
  FaUserCircle
} from 'react-icons/fa';
import api from '../services/api';
import socketService from '../services/socket';
import { getAssetImage } from '../utils/imageHelper';
import 'swiper/css';
import 'swiper/css/pagination';
import './Home.css';

const Home = () => {
  const [assets, setAssets] = useState([]);
  const [tickerData, setTickerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('popular');

  // Default coin data
  const defaultCoins = [
    { id: 1, symbol: 'BTC', name: 'Bitcoin', price: 90967.85, priceChange: -1.86 },
    { id: 2, symbol: 'ETH', name: 'Ethereum', price: 3133.41, priceChange: -1.55 },
    { id: 3, symbol: 'BNB', name: 'BNB', price: 893.8, priceChange: -1.79 },
    { id: 4, symbol: 'LTC', name: 'Litecoin', price: 82.32, priceChange: -2.05 },
    { id: 5, symbol: 'TRX', name: 'Tron', price: 0.2862, priceChange: 1.38 },
  ];

  // Default ticker data
  const defaultTickerData = [
    { symbol: 'BTC/USDT', price: 90967.85, change: -1.86 },
    { symbol: 'ETH/USDT', price: 3133.41, change: -1.55 },
    { symbol: 'TRX/USDT', price: 0.2862, change: 1.38 },
  ];

  // Fetch prices from Binance API (real-time from TradingView source)
  const fetchTickerPrices = useCallback(async () => {
    try {
      const symbols = ['BTCUSDT', 'ETHUSDT', 'TRXUSDT'];
      const tickers = await Promise.all(
        symbols.map(async (symbol) => {
          try {
            const response = await fetch(`https://api.binance.com/api/v3/ticker/24hr?symbol=${symbol}`);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            const currentPrice = parseFloat(data.lastPrice);
            const previousPrice = parseFloat(data.prevClosePrice);
            const changePercent = previousPrice > 0 
              ? ((currentPrice - previousPrice) / previousPrice) * 100 
              : 0;
            
            return {
              symbol: symbol.replace('USDT', '/USDT'),
              price: currentPrice,
              change: changePercent
            };
          } catch (error) {
            console.error(`Error fetching ${symbol}:`, error);
            return null;
          }
        })
      );
      
      const validTickers = tickers.filter(Boolean);
      if (validTickers.length > 0) {
        setTickerData(prevTickers => {
          // Only update if data actually changed to avoid unnecessary re-renders
          if (JSON.stringify(prevTickers) !== JSON.stringify(validTickers)) {
            return validTickers;
          }
          return prevTickers;
        });
      }
    } catch (error) {
      console.error('Error fetching ticker prices:', error);
      // Don't overwrite existing data on error
    }
  }, []);

  // Fetch price from Yahoo Finance API for XAU and OIL
  const fetchYahooFinancePrice = async (yahooSymbol) => {
    try {
      const response = await fetch(`https://query1.finance.yahoo.com/v8/finance/chart/${yahooSymbol}?interval=1d&range=1d`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data.chart && data.chart.result && data.chart.result[0]) {
        const result = data.chart.result[0];
        const currentPrice = result.meta?.regularMarketPrice || result.meta?.previousClose;
        const previousPrice = result.meta?.previousClose;
        const changePercent = previousPrice > 0 
          ? ((currentPrice - previousPrice) / previousPrice) * 100 
          : 0;
        return { price: currentPrice, priceChange: changePercent };
      }
      return null;
    } catch (error) {
      console.error(`Yahoo Finance API error for ${yahooSymbol}:`, error);
      return null;
    }
  };

  // Fetch all asset prices from Binance API (real-time updates)
  const fetchAllAssetPrices = useCallback(async (assetList) => {
    try {
      // Use provided assetList or current assets or default
      const currentAssets = assetList || (assets.length > 0 ? assets : defaultCoins);
      
      // Map of symbol to Binance symbol
      const symbolMap = {
        'BTC': 'BTCUSDT',
        'ETH': 'ETHUSDT',
        'BNB': 'BNBUSDT',
        'XRP': 'XRPUSDT',
        'ADA': 'ADAUSDT',
        'SOL': 'SOLUSDT',
        'DOT': 'DOTUSDT',
        'DOGE': 'DOGEUSDT',
        'MATIC': 'MATICUSDT',
        'AVAX': 'AVAXUSDT',
        'LTC': 'LTCUSDT',
        'XLM': 'XLMUSDT',
        'TRX': 'TRXUSDT',
        'ETC': 'ETCUSDT',
        'LINK': 'LINKUSDT',
        'ZRX': 'ZRXUSDT',
        'XAU': 'GC=F', // Gold futures (Yahoo Finance)
        'OIL': 'CL=F'  // Crude Oil futures (Yahoo Finance)
      };

      // Fetch prices for all assets
      const pricePromises = currentAssets.map(async (asset) => {
        const symbolKey = asset.symbol?.toUpperCase();
        const binanceSymbol = symbolMap[symbolKey];
        
        // If it's XAU or OIL, use Yahoo Finance API
        if (symbolKey === 'XAU' || symbolKey === 'OIL') {
          const yahooSymbol = symbolMap[symbolKey];
          if (yahooSymbol) {
            try {
              const priceData = await fetchYahooFinancePrice(yahooSymbol);
              if (priceData) {
                return {
                  ...asset,
                  price: priceData.price,
                  priceChange: priceData.priceChange
                };
              }
            } catch (error) {
              console.error(`Error fetching price for ${asset.symbol}:`, error);
            }
          }
          return asset;
        }

        // For other assets, use Binance API
        if (!binanceSymbol) {
          return asset;
        }

        try {
          const response = await fetch(`https://api.binance.com/api/v3/ticker/24hr?symbol=${binanceSymbol}`);
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          const data = await response.json();
          const currentPrice = parseFloat(data.lastPrice);
          const previousPrice = parseFloat(data.prevClosePrice);
          const changePercent = previousPrice > 0 
            ? ((currentPrice - previousPrice) / previousPrice) * 100 
            : 0;
          
          return {
            ...asset,
            price: currentPrice,
            priceChange: changePercent
          };
        } catch (error) {
          console.error(`Error fetching price for ${asset.symbol}:`, error);
          // Return original asset if fetch fails
          return asset;
        }
      });

      const updatedAssets = await Promise.all(pricePromises);
      
      // Only update if data actually changed
      setAssets(prevAssets => {
        if (JSON.stringify(prevAssets) !== JSON.stringify(updatedAssets)) {
          return updatedAssets;
        }
        return prevAssets;
      });
    } catch (error) {
      console.error('Error fetching asset prices:', error);
      // Don't overwrite existing data on error
    }
  }, [assets]);

  useEffect(() => {
    // Set initial ticker data
    setTickerData(defaultTickerData);
    
    let assetListCache = [];
    
    // Load assets first
    loadAssets().then((loadedAssets) => {
      assetListCache = loadedAssets;
      // After assets are loaded, fetch real-time prices
      fetchAllAssetPrices(loadedAssets);
    });
    
    // Fetch initial ticker prices immediately
    fetchTickerPrices();
    
    // Update ticker prices every 1 second
    const tickerInterval = setInterval(() => {
      fetchTickerPrices();
    }, 1000);
    
    // Update all asset prices every 2 seconds for better performance
    const assetInterval = setInterval(() => {
      // Use cached asset list or current assets
      const assetsToUpdate = assetListCache.length > 0 
        ? assetListCache 
        : (assets.length > 0 ? assets : defaultCoins);
      fetchAllAssetPrices(assetsToUpdate);
    }, 2000); // Update every 2 seconds for all assets
    
    // Also listen to WebSocket for asset updates (if needed)
    socketService.connect();
    const handlePriceUpdate = (updatedAssets) => {
      // Only update if WebSocket has new data structure
      if (updatedAssets && updatedAssets.length > 0) {
        assetListCache = updatedAssets;
        // Merge with real-time Binance prices
        fetchAllAssetPrices(updatedAssets);
      }
    };
    
    socketService.onPriceUpdate(handlePriceUpdate);

    // Cleanup on unmount
    return () => {
      clearInterval(tickerInterval);
      clearInterval(assetInterval);
      socketService.off('price_update', handlePriceUpdate);
      socketService.disconnect();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAssets = async () => {
    try {
      const data = await api.getAssets();
      const assetsList = data.length > 0 ? data : defaultCoins;
      setAssets(assetsList);
      return assetsList;
    } catch (error) {
      setAssets(defaultCoins);
      return defaultCoins;
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [];

  const tabs = [
    { id: 'popular', label: 'Popular coins', link: null },
    { id: 'foreign', label: 'Foreign currency', link: '/foreign-currency' },
    { id: 'international', label: 'International', link: '/international' },
  ];

  const displayAssets = assets.length > 0 ? assets : defaultCoins;

  return (
    <div className="home-page">
      {/* Header */}
      <header className="home-header">
        <div className="header-left">
          <img 
            src="/images/assets/6170046161289415862.jpg" 
            alt="Streaming" 
            className="header-logo"
          />
        </div>
        <div className="header-avatar">
          <img 
            src="/images/assets/6170046161289415899.jpg" 
            alt="Support" 
            className="avatar-image"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              // Open LINE contact for admin/support
              const lineUrl = 'https://lin.ee/uGDyY5w';
              window.open(lineUrl, '_blank');
            }}
            onError={(e) => {
              // Fallback to default icon if image not found
              e.target.style.display = 'none';
              const parent = e.target.parentNode;
              if (parent && !parent.querySelector('.avatar-circle')) {
                const fallback = document.createElement('div');
                fallback.className = 'avatar-circle';
                fallback.style.cursor = 'pointer';
                fallback.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/></svg>';
                fallback.onclick = () => {
                  const lineUrl = 'https://lin.ee/uGDyY5w';
                  window.open(lineUrl, '_blank');
                };
                parent.appendChild(fallback);
              }
            }}
          />
        </div>
      </header>

      <div className="home-content">
        {/* Banner Slider */}
        <section className="banner-section">
          <Swiper
            modules={[Autoplay, Pagination]}
            spaceBetween={0}
            slidesPerView={1}
            autoplay={{ delay: 4000, disableOnInteraction: false }}
            pagination={{ clickable: true }}
            loop={true}
            className="banner-slider"
          >
            <SwiperSlide>
              <div className="banner-slide">
                <img 
                  src="/images/banners/banner1.png" 
                  alt="Banner 1"
                  className="banner-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className="banner-slide">
                <img 
                  src="/images/banners/banner2.png" 
                  alt="Banner 2"
                  className="banner-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </SwiperSlide>
          </Swiper>
        </section>

        {/* Price Ticker Banner */}
        <section className="ticker-section">
          <Swiper
            modules={[Autoplay, Pagination]}
            spaceBetween={0}
            slidesPerView={1}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            pagination={{ clickable: true }}
            loop={true}
            className="ticker-slider"
          >
            <SwiperSlide>
              <div className="ticker-slide">
                {(tickerData.length > 0 ? tickerData : defaultTickerData).map((ticker, index) => (
                  <div key={`${ticker.symbol}-${index}`} className="ticker-item">
                    <span className="ticker-symbol">{ticker.symbol}</span>
                    <span className={`ticker-price ${ticker.change >= 0 ? 'positive' : 'negative'}`}>
                      {ticker.price.toLocaleString(undefined, { 
                        minimumFractionDigits: ticker.price < 1 ? 4 : 2,
                        maximumFractionDigits: ticker.price < 1 ? 4 : 2
                      })}
                    </span>
                    <span className={`ticker-change ${ticker.change >= 0 ? 'positive' : 'negative'}`}>
                      {ticker.change >= 0 ? '+' : ''}{ticker.change.toFixed(2)}%
                    </span>
                  </div>
                ))}
              </div>
            </SwiperSlide>
          </Swiper>
        </section>

        {/* Tabs */}
        <section className="tabs-section">
          {tabs.map((tab) => (
            tab.link ? (
              <Link
                key={tab.id}
                to={tab.link}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              >
                {tab.label}
              </Link>
            ) : (
              <button
                key={tab.id}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            )
          ))}
        </section>

        {/* Coin List */}
        <section className="coins-section">
          {loading ? (
            <div className="loading-container">
              <div className="spinner" />
            </div>
          ) : (
            <div className="coins-list">
              {displayAssets.slice(0, 10).map((asset, index) => (
                <motion.div
                  key={asset.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link to={`/trading/${asset.id}`} className="coin-row">
                    <div className="coin-left">
                      <div className="coin-icon">
                        <img 
                          src={getAssetImage(asset.symbol)} 
                          alt={asset.name}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="coin-placeholder" style={{ display: 'none' }}>
                          {asset.symbol?.charAt(0)}
                        </div>
                      </div>
                      <span className="coin-symbol">{asset.symbol === 'XAU' ? 'GOLD' : asset.symbol}</span>
                    </div>
                    
                    <div className="coin-right">
                      <div className="coin-price-info">
                        <span className="coin-price">{asset.price?.toLocaleString()}</span>
                        <span className="coin-price-usd">${asset.price?.toLocaleString()}</span>
                      </div>
                      <div className={`coin-change-badge ${asset.priceChange >= 0 ? 'positive' : 'negative'}`}>
                        {asset.priceChange >= 0 ? '+' : ''}{asset.priceChange?.toFixed(2)}%
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        <Link to="/" className="nav-item active">
          <FaHome />
          <span>Home</span>
        </Link>
        <Link to="/trading" className="nav-item">
          <FaChartBar />
          <span>Trade</span>
        </Link>
        <Link to="/history" className="nav-item">
          <FaEye />
          <span>History</span>
        </Link>
        <Link to="/profile" className="nav-item">
          <FaFileAlt />
          <span>Profile</span>
        </Link>
      </nav>
    </div>
  );
};

export default Home;
